import React, { useEffect, useMemo, useRef, useState } from 'react';
import BlogModal from './BlogModal';
import '../../assets/styles/hormoneTestsSlider.scss';

export type HormoneTest = {
  id: string | number;
  image: string;
  date: string;
  title: string;
  content: string;
  videoUrl?: string;
};

type Props = {
  title?: string;
  items: HormoneTest[];
  viewAllLink?: string;
  titleColor?: string;
  buttonColor?: string;
  accentColor?: string;
};

const HormoneTestsSlider: React.FC<Props> = ({
  title = 'Hormon Testleri',
  items,
  viewAllLink,
  titleColor = '#2cadd2',
  buttonColor = '#2cadd2',
  accentColor = '#2cadd2',
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [selectedTest, setSelectedTest] = useState<HormoneTest | null>(null);
  const [showAll, setShowAll] = useState(false);

  const styleVars = useMemo(
    () =>
      ({
        '--hts-title-color': titleColor,
        '--hts-button-color': buttonColor,
        '--hts-accent-color': accentColor,
      } as React.CSSProperties),
    [titleColor, buttonColor, accentColor]
  );

  // Kaç sayfa var (her sayfada 3 kart)
  const totalPages = Math.ceil(items.length / 3);

  // Scroll pozisyonuna göre aktif sayfa index'i takip et
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || showAll) return;

    const onScroll = () => {
      const scrollLeft = el.scrollLeft;
      const scrollWidth = el.scrollWidth - el.clientWidth;
      const pageIndex = Math.round((scrollLeft / scrollWidth) * (totalPages - 1));
      setActiveIndex(Math.max(0, Math.min(totalPages - 1, pageIndex)));
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [totalPages, showAll]);

  // Otomatik kayma (5 saniyede bir sayfa)
  useEffect(() => {
    if (isUserInteracting || totalPages <= 1 || showAll) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        const nextIndex = prev + 1 >= totalPages ? 0 : prev + 1;
        goToPage(nextIndex);
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [totalPages, isUserInteracting, showAll]);

  // Kullanıcı etkileşimini algıla
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || showAll) return;

    const handleInteraction = () => {
      setIsUserInteracting(true);
      setTimeout(() => setIsUserInteracting(false), 10000);
    };

    el.addEventListener('touchstart', handleInteraction);
    el.addEventListener('mousedown', handleInteraction);

    return () => {
      el.removeEventListener('touchstart', handleInteraction);
      el.removeEventListener('mousedown', handleInteraction);
    };
  }, [showAll]);

  const goToPage = (pageIndex: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const scrollWidth = el.scrollWidth - el.clientWidth;
    const scrollLeft = (scrollWidth / (totalPages - 1)) * pageIndex;
    el.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  };

  return (
    <section className="hormone-tests-slider" style={styleVars}>
      <div className="hts-container">
        <div className="hts-header">
          <h2 className="hts-title">{title}</h2>
        </div>

        {!showAll ? (
          <>
            <div className="hts-scroller" ref={scrollerRef}>
              {items.map((item) => (
                <article key={item.id} className="hts-card">
                  <div className="hts-image">
                    <img src={item.image} alt={item.title} loading="lazy" />
                  </div>
                  <div className="hts-content">
                    <time className="hts-date">{item.date}</time>
                    <h3 className="hts-card-title">{item.title}</h3>
                    <button
                      onClick={() => setSelectedTest(item)}
                      className="hts-button"
                    >
                      Devamını Oku
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <div className="hts-accent-line"></div>
                  </div>
                </article>
              ))}
            </div>

            <div className="hts-footer">
              <button onClick={() => setShowAll(true)} className="hts-view-all">
                Tümünü Görüntüle
              </button>
              <div className="hts-dots">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    className={`hts-dot ${i === activeIndex ? 'active' : ''}`}
                    onClick={() => goToPage(i)}
                    aria-label={`${i + 1}. sayfaya git`}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="hts-grid">
              {items.map((item) => (
                <article key={item.id} className="hts-card">
                  <div className="hts-image">
                    <img src={item.image} alt={item.title} loading="lazy" />
                  </div>
                  <div className="hts-content">
                    <time className="hts-date">{item.date}</time>
                    <h3 className="hts-card-title">{item.title}</h3>
                    <button
                      onClick={() => setSelectedTest(item)}
                      className="hts-button"
                    >
                      Devamını Oku
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <div className="hts-accent-line"></div>
                  </div>
                </article>
              ))}
            </div>
            <div className="hts-footer">
              <button onClick={() => setShowAll(false)} className="hts-view-all">
                Daha Az Göster
              </button>
            </div>
          </>
        )}
      </div>

      {selectedTest && (
        <BlogModal
          isOpen={!!selectedTest}
          onClose={() => setSelectedTest(null)}
          title={selectedTest.title}
          date={selectedTest.date}
          image={selectedTest.image}
          content={selectedTest.content}
          videoUrl={selectedTest.videoUrl}
        />
      )}
    </section>
  );
};

export default HormoneTestsSlider;
